import type { ComponentType, FC } from "react";
import { useMemo } from "react";

import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { ControlledErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import { TimeoutErrorGate } from "app/pages/sales-intelligence/common-components/error-state/components/TimeoutErrorGate";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";

import type { ContactsResultsTableProps } from "../components/ContactsResultsTable/types";

import { StyledTableErrorState } from "./styles";

export interface IContactSearchErrorBoundaryProps {
    isError?: boolean;
    error?: unknown;
    onRefetchData?: () => Promise<unknown> | undefined;
}

type Props = ContactsResultsTableProps & IContactSearchErrorBoundaryProps;

function withContactSearchErrorBoundary(TableComponent: FC<ContactsResultsTableProps>): ComponentType<Props> {
    return function WrappedWithErrorBoundary(componentProps: Props) {
        const { onRefetchData, isError, error } = componentProps;
        const hasError = !componentProps.isLoading && !!isError;

        const boundaryError = useMemo(
            () =>
                hasError
                    ? new SiError(
                          "Contacts search request failed",
                          {
                              category: errorCategoryMap.api,
                              component: "ContactsResultsTable",
                              operation: "contactsSearch",
                          },
                          { cause: error },
                      )
                    : undefined,
            [hasError, error],
        );

        const renderCtaButton = () => {
            return (
                <Button
                    type="primary"
                    onClick={() => onRefetchData?.()}
                >
                    {translate("si.common.refresh")}
                </Button>
            );
        };

        const renderErrorState = () => (
            <StyledTableErrorState
                title=""
                subtitle={translate("si.common.something-went-wrong.refresh-page")}
                iconName="no-data-tree"
                CtaButtons={renderCtaButton}
            />
        );

        return (
            <TimeoutErrorGate
                isError={hasError}
                error={error}
            >
                <ControlledErrorBoundaryWithLogging
                    error={boundaryError}
                    resetKeys={[componentProps.isLoading, componentProps.data]}
                    fallback={renderErrorState()}
                >
                    <TableComponent {...componentProps} />
                </ControlledErrorBoundaryWithLogging>
            </TimeoutErrorGate>
        );
    };
}

export default withContactSearchErrorBoundary;
